import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import { createAbsoluteUrl } from "utils/constants";
import { useGlobalSlice } from "app/slice";
import { selectUser } from "app/slice/selectors";
import { useManageProfileSlice } from "app/pages/Customer/ManageProfile/slice";

import { Avatar, AvatarFallback, AvatarImage } from 'app/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover';

const myAccountItems = [
	{ to: "/my-account/dashboard", label: "My Dashboard" },
	{ to: "/my-account/payment-history", label: "Payment History" },
	{ to: "/my-account/manage-profile", label: "Manage Profile" },
	{ to: "/my-account/change-password", label: "Change Password" },
	{ to: "/my-account/sim-service", label: "SIM Service" },
  // { to: "/support-tickets", label: "Support" },
  { to: createAbsoluteUrl("/support-tickets"), label: "Support", target: "_self" },
  { to: createAbsoluteUrl(), label: "Sign Out" },
];

export const MobileMyAccount = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [localProfileData, setLocalProfileData] = useState<any>(null);
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { actions } = useGlobalSlice();
  const { useLazyGetProfileQuery } = useManageProfileSlice();
  const [
    getProfile,
    {
      data: profileData,
      isLoading: isProfileLoading,
      isError: isProfileError,
    },
  ] = useLazyGetProfileQuery();

  useEffect(() => {
    if (user?._id) {
      getProfile({ customerId: user._id });
    } else {
      // Clear local profile data when user logs out
      setLocalProfileData(null);
    }
  }, [user?._id, getProfile]);

  useEffect(() => {
    // Update local profile data when API response changes
    if (profileData && user?._id) {
      setLocalProfileData(profileData);
    }
  }, [profileData, user?._id]);

  // Reset popover state when user authentication changes
  useEffect(() => {
    setIsOpen(false);
  }, [user?._id]);

  const handleAvatarClick = () => {
    if (!user?._id) {
      navigate('/login');
    } else {
      setIsOpen(!isOpen);
    }
  };

  const handleSignOut = () => {
    dispatch(actions.setUser({ 
      user: null,
      rememberDevice: false
    }));
    setLocalProfileData(null); // Clear local profile data on sign out
    // navigate("/");
    window.location.href = createAbsoluteUrl();
    setIsOpen(false);
  };

  // Only show profile data if user is authenticated
  const shouldShowProfileData = user?._id && localProfileData;
  const profileImage = shouldShowProfileData ? localProfileData?.data?.image : null;
  const profileFirstName = shouldShowProfileData ? localProfileData?.data?.firstName : null;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Avatar 
          className="lg:hidden items-center justify-center bg-[#BFBFBF1F] size-9 rounded-full cursor-pointer"
          onClick={handleAvatarClick}
          tabIndex={0}
          aria-label={user?._id ? "Open account menu" : "Sign in to your account"}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleAvatarClick();
            }
          }}
        >
          <AvatarImage 
            src={user ? profileImage : "/assets/profile.svg"}  
            alt="Profile" 
            className={profileImage ? "rounded-full size-full" : "size-5"}
          />
          <AvatarFallback className="bg-[#BFBFBF1F] uppercase text-black text-sm font-medium leading-3">
            {user?.firstName?.charAt(0) || profileFirstName?.charAt(0) || "U"}
          </AvatarFallback>
        </Avatar>
      </PopoverTrigger>
      {user?._id && (
        <PopoverContent className="overflow-hidden p-1 mr-5 w-fit">
          {myAccountItems.map((item, index) => (
            <div key={index} className="hover:bg-[#F6F6F6] px-2 py-0.5 sm:py-1 rounded">
              {item.label === "Sign Out" ? (
                <div
                  className="flex w-full text-sm leading-7 sm:leading-[1.8125rem] text-[#FE4D01] hover:text-[#E44501] font-semibold cursor-pointer"
                  onClick={handleSignOut}
                >
                  {item.label}
                </div>
              ) : item.target ? (
                <a
                  href={item.to}
                  target={item.target || '_blank'}
                  rel="noopener noreferrer"
                  className="flex w-full text-sm leading-7 sm:leading-[1.8125rem] hover:text-[#FE4D01] text-[#464646] font-medium"
                  onClick={() => setIsOpen(false)}
                >
                  {item.label}
                </a>
              ) : (
                <NavLink
                  to={item.to || ""}
                  onClick={() => setIsOpen(false)}
                  className={({ isActive }) =>
                    `flex w-full text-sm leading-7 sm:leading-[1.8125rem] hover:text-[#FE4D01] ${
                      isActive ? "text-[#1D1D1B] font-semibold" : "text-[#464646] font-medium"
                    }`
                  }
                  tabIndex={0}
                  aria-label={item.label}
                >
                  {item.label}
                </NavLink>
              )}
            </div>
          ))}
        </PopoverContent>
      )}
    </Popover>
  );
};
